// Data hooks with live refresh: every 2 s while something is running, otherwise rarely.
import type {
  AdminJournalResponse,
  AdminReadiness,
  AdminSettings,
  AdminStoriesResponse,
  AdminStoryDetail,
  AdminStoryStatus,
  AdminVoicesResponse,
} from '@fonus/shared';
import { useQuery } from '@tanstack/react-query';
import { api } from './api.ts';

export const LIVE_MS = 2_000;

export const useReadiness = () =>
  useQuery({
    queryKey: ['readiness'],
    queryFn: () => api<AdminReadiness>('/admin/readiness'),
    refetchInterval: (q) => (q.state.data?.busy ? LIVE_MS : 15_000),
  });

export const useStories = (status?: AdminStoryStatus) =>
  useQuery({
    queryKey: ['stories', status ?? 'all'],
    queryFn: () => api<AdminStoriesResponse>(`/admin/stories${status ? `?status=${status}` : ''}`),
    refetchInterval: (q) => (q.state.data?.items.some((i) => i.busy) ? LIVE_MS : 20_000),
  });

export const useStory = (id: string) =>
  useQuery({
    queryKey: ['story', id],
    queryFn: () => api<AdminStoryDetail>(`/admin/stories/${id}`),
    refetchInterval: (q) => (q.state.data?.busy ? LIVE_MS : false),
  });

export const useVoices = () =>
  useQuery({
    queryKey: ['voices'],
    queryFn: () => api<AdminVoicesResponse>('/admin/voices'),
    refetchInterval: (q) => {
      const s = q.state.data?.lastJob?.status;
      return s === 'QUEUED' || s === 'RUNNING' ? LIVE_MS : false;
    },
  });

export const useJournal = (filter: 'all' | 'errors' | 'active') =>
  useQuery({
    queryKey: ['journal', filter],
    queryFn: () => api<AdminJournalResponse>(`/admin/journal?filter=${filter}`),
    refetchInterval: (q) =>
      q.state.data?.groups.some((g) => g.status === 'active') ? LIVE_MS : 10_000,
  });

export const useSettings = () =>
  useQuery({ queryKey: ['settings'], queryFn: () => api<AdminSettings>('/admin/settings') });
